import { cn } from '@atlas/ui-primitives'
import { DiffKind, diffLines } from '@/domain/line-diff'

export interface PromptBodyDiffProps {
	/** The older body — what the comparison is FROM. */
	oldBody: string
	/** The newer body — what the comparison is TO. */
	newBody: string
	oldLabel: string
	newLabel: string
}

const LINE_STYLE: Record<DiffKind, string> = {
	[DiffKind.UNCHANGED]: 'text-ds-text-muted',
	// Colour is never the only signal: the +/- gutter marks carry the same information
	// for a reader who cannot distinguish them.
	[DiffKind.ADDED]: 'bg-ds-success/10 text-ds-text',
	[DiffKind.REMOVED]: 'bg-ds-danger/10 text-ds-text-muted line-through',
}

const MARK: Record<DiffKind, string> = {
	[DiffKind.UNCHANGED]: ' ',
	[DiffKind.ADDED]: '+',
	[DiffKind.REMOVED]: '-',
}

/**
 * Unified line diff of two prompt bodies.
 *
 * Unified rather than side-by-side: prompt bodies are narrow and long, and two columns
 * at this width would wrap every line and cost the alignment that makes a diff readable.
 *
 * Presentational + storied.
 */
export function PromptBodyDiff({ oldBody, newBody, oldLabel, newLabel }: PromptBodyDiffProps) {
	const lines = diffLines(oldBody, newBody)
	const changed = lines.some((l) => l.kind !== DiffKind.UNCHANGED)

	return (
		<div>
			<p className="mb-2 text-ds-sm text-ds-text-muted">
				Comparing <span className="font-mono text-ds-text">{oldLabel}</span> →{' '}
				<span className="font-mono text-ds-text">{newLabel}</span>
			</p>

			{changed ? (
				<div className="overflow-x-auto rounded-ds-card border border-ds-border bg-ds-surface">
					{/* A list, not a table: there is one cell of content per row, and a table
					    would announce a column structure that carries no meaning. */}
					<ul className="min-w-full py-1 font-mono text-xs leading-relaxed">
						{lines.map((line, index) => (
							<li
								// Index is part of the key deliberately: identical text can legitimately
								// appear on several lines, so text alone is not unique.
								key={`${line.kind}-${line.oldLine ?? 'x'}-${line.newLine ?? 'x'}-${index}`}
								className={cn('flex gap-3 whitespace-pre px-3', LINE_STYLE[line.kind])}
							>
								<span aria-hidden="true" className="w-3 shrink-0 select-none text-ds-text-subtle">
									{MARK[line.kind]}
								</span>
								<span className="w-8 shrink-0 select-none text-right text-ds-text-subtle">{line.newLine ?? ''}</span>
								{/* The kind is named for assistive tech, since the colour and the
								    gutter mark are both visual. */}
								<span className="sr-only">{line.kind === DiffKind.UNCHANGED ? '' : `${line.kind}: `}</span>
								<span>{line.text === '' ? ' ' : line.text}</span>
							</li>
						))}
					</ul>
				</div>
			) : (
				<p className="rounded-ds-card border border-dashed border-ds-border p-4 text-center text-ds-sm text-ds-text-muted">
					The body is identical in both versions.
				</p>
			)}
		</div>
	)
}
